import Slider from 'react-slick';
import { useRef } from 'react';
// @mui
import {
  Dialog,
  DialogContent,
  Stack,
  Paper,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
// hooks
import useResponsive from "../../../hooks/useResponsive";
// components
import DIOText from '../../../components/DIOText';
import Label from '../../../components/Label';
import { CarouselDots, CarouselArrows } from '../../../components/carousel';

const BlueText = styled('span')(({ theme }) => ({
  color: theme.palette.secondary.main,
}));

const CarouselTypo = styled(Typography)(({ theme }) => ({
  ...theme.typography.body2,
  [theme.breakpoints.up('sm')]: {
    ...theme.typography.body1,
  }
}));

const PROCESSES = {
  "developer": [
    {
      "id": 1,
      "role": "백엔드 개발자",
      "label": ["#결제 기능 개발 경험이 없는", "#커머스 스타트업"],
      "line1": [
        <CarouselTypo key="1-1"><b>Django 백엔드</b> 개발자</CarouselTypo>
      ],
      "line2": [
        <CarouselTypo key="2-1">- 기존 홈페이지에 결제 서비스 구축</CarouselTypo>,
        <CarouselTypo key="2-2">- 신규로 오픈할 글로벌 서비스 랜딩 페이지 개발</CarouselTypo>
      ],
      "line3": [
        <CarouselTypo key="3-1"><b>📌 결제 기능 개발을 해보신 분!</b></CarouselTypo>,
      ],
    },
    {
      "id": 2,
      "role": "백엔드 개발자",
      "label": ["#매일 금융 데이터 스크래핑을 해야하는", "#핀테크 스타트업"],
      "line1": [
        <CarouselTypo key="1-1"><b>FastAPI 백엔드</b> 개발자</CarouselTypo>
      ],
      "line2": [
        <CarouselTypo key="2-1">- 대용량 텍스트 데이터를 크롤링하고, 가상머신에서 전처리하여 DB에 저장해야합니다.</CarouselTypo>,
        <CarouselTypo key="2-2">- 팀에서 API 사용법 및 데이터에 대한 자세한 가이드를 제공할 예정입니다.</CarouselTypo>
      ],
      "line3": [
        <CarouselTypo key="3-1"><b>📌 API 개발 경험, 클라우드에 배포해본 경험</b>이 있는 개발자분!</CarouselTypo>,
      ],
    },
    {
      "id": 3,
      "role": "프론트엔드 개발자",
      "label": ["#프론트엔드 개발자가 갑자기 퇴사해버린", "#B2B SaaS 스타트업"],
      "line1": [
        <CarouselTypo key="1-1"><b>React 프론트엔드</b> 개발자</CarouselTypo>
      ],
      "line2": [
        <CarouselTypo key="2-1">- 기획 및 디자인 작업물 확인(Figma) 후 개발 진행</CarouselTypo>,
        <CarouselTypo key="2-2">- 매주 월/목 전체 Zoom 회의 통해 업무 점검 </CarouselTypo>
      ],
      "line3": [
        <CarouselTypo key="3-1"><b>📌 SQL, NoSQL 데이터베이스 사용 경험</b>이 있으신 분이면 좋겠습니다!</CarouselTypo>,
      ],
    },
    {
      "id": 4,
      "role": "테크 리드",
      "label": ["#주니어 개발자로만 구성된", "#커뮤니티앱 스타트업"],
      "line1": [
        <CarouselTypo key="1-1">주니어 개발 팀을 도와주실 <b>시니어 개발자</b>를 모십니다.</CarouselTypo>
      ],
      "line2": [
        <CarouselTypo key="2-1">- 프론트, 백엔드 이슈 발생 시 해결 방안 제시</CarouselTypo>,
        <CarouselTypo key="2-2">- 팀 개발 스택 : React, Nest.js</CarouselTypo>
      ],
      "line3": [
        <CarouselTypo key="3-1"><b>📌 프론트엔드 개발자에게도 도움 주실</b> 수 있으면 더욱 좋겠습니다!</CarouselTypo>,
      ],
    },
  ],
  "designer": [
    {
      "id": 1,
      "role": "UX/UI 디자이너",
      "label": ["#신규 기능 개발 디자인이 필요한", "#명품 중고거래앱 스타트업"],
      "line1": [
        <CarouselTypo key="1-1"><b>UI/UX 디자이너</b> (PC/모바일)</CarouselTypo>
      ],
      "line2": [
        <CarouselTypo key="2-1">- 신규 기능(명품 소개 매거진)에 대한 디자인 시스템 및 컴포넌트 제작</CarouselTypo>,
        <CarouselTypo key="2-2">- 주요 화면 : 제품 프로필, 브랜드별 상품 리스트, 영상 콘텐츠, 채팅 등</CarouselTypo>
      ],
      "line3": [
        <CarouselTypo key="3-1"><b>📌 바로 업무 가능</b>하신 분</CarouselTypo>,
        <CarouselTypo key="3-2"><b>📌 첨부된 기획문서 참고</b>해주세요!</CarouselTypo>,
      ],
    },
    {
      "id": 2,
      "role": "콘텐츠 디자이너",
      "label": ["#쇼핑몰 내 배너/상세페이지 디자인이 필요한", "#화장품 커머스"],
      "line1": [
        <CarouselTypo key="1-1"><b>콘텐츠 디자이너</b></CarouselTypo>
      ],
      "line2": [
        <CarouselTypo key="2-1">- 인스타그램 등의 SNS 채널에 올릴 소재 제작</CarouselTypo>,
        <CarouselTypo key="2-2">- 앱 내 배너 디자인</CarouselTypo>,
        <CarouselTypo key="2-3">- 앱 내 상세 이미지 디자인</CarouselTypo>
      ],
      "line3": [
        <CarouselTypo key="3-1"><b>📌 뷰티 커머스 경험자 분</b>이면 좋겠습니다!</CarouselTypo>,
      ],
    },
  ],
  "marketer": [
    {
      "id": 1,
      "role": "퍼포먼스 마케터",
      "label": ["#영어권 마케팅이 필요한", "#명품 커머스 스타트업"],
      "line1": [
        <CarouselTypo key="1-1"><b>퍼포먼스 마케터</b></CarouselTypo>
      ],
      "line2": [
        <CarouselTypo key="2-1">- 앱 마케팅 계획 및 실행</CarouselTypo>,
        <CarouselTypo key="2-2">- Amplitude, AppsFlyer 등 툴 세팅 및 분석</CarouselTypo>,
        <CarouselTypo key="2-3">- 주니어 마케팅 리드</CarouselTypo>,
      ],
      "line3": [
        <CarouselTypo key="3-1"><b>📌 해외 서비스(영어권) 마케팅 경험</b>이 있으신 분!</CarouselTypo>,
      ],
    },
    {
      "id": 2,
      "role": "퍼포먼스 마케터",
      "label": ["#매출이 올라서 마케터가 필요해진", "#쥬얼리 커머스 스타트업"],
      "line1": [
        <CarouselTypo key="1-1"><b>퍼포먼스 마케터</b></CarouselTypo>
      ],
      "line2": [
        <CarouselTypo key="2-1">- 페이스북, 네이버 GFA 광고</CarouselTypo>,
        <CarouselTypo key="2-2">- 오프라인 매장만 운영하다가 자체몰 개발(카페24)을 완료하여 디지털 마케팅을 시작하는 단계</CarouselTypo>
      ],
      "line3": [
        <CarouselTypo key="3-1"><b>📌 커머스 마케팅 경력</b>이 있으신 분</CarouselTypo>,
        <CarouselTypo key="3-2"><b>📌 월 200만원 정도 규모의 광고비</b>를 운영해본 경험이 있으신분 (소규모 팀 경험이 있으신 분)</CarouselTypo>,
      ],
    },
    {
      "id": 3,
      "role": "콘텐츠 마케터",
      "label": ["#정식 런칭을 앞둔", "#영어 교육 스타트업"],
      "line1": [
        <CarouselTypo key="1-1"><b>콘텐츠 마케터</b></CarouselTypo>
      ],
      "line2": [
        <CarouselTypo key="2-1">- 정식 버젼 런칭 시, 런칭 마케팅 (런칭 예상일정 : 7월초)</CarouselTypo>,
        <CarouselTypo key="2-2">- 광고 크리에이티브 기획 및 제작</CarouselTypo>,
        <CarouselTypo key="2-2">- 소셜 미디어 채널 기획/운영</CarouselTypo>,
      ],
      "line3": [
        <CarouselTypo key="3-1"><b>📌 글로벌 마케팅 경험 혹은 엔터/교육 쪽 서비스 경험</b>이 있으신 분이면 좋겠습니다!</CarouselTypo>,
      ],
    },
  ],
  "product-manager": [
    {
      "id": 1,
      "role": "서비스 기획자",
      "label": ["#신규 기능 개발 디자인이 필요한", "#모빌리티 스타트업"],
      "line1": [
        <CarouselTypo key="1-1"><b>신규 서비스 기획자</b></CarouselTypo>
      ],
      "line2": [
        <CarouselTypo key="2-1">- <b>킥보드 재고 관리 서비스를 기획</b>해주실 분</CarouselTypo>,
        <CarouselTypo key="2-2">- 현재 자사가 운영중인 공유 킥보드 서비스의 대리점에서 사용할 서비스임</CarouselTypo>,
        <CarouselTypo key="2-3">- 상세 디자인을 지원해드릴 팀 내 UI 디자이너는 있습니다.</CarouselTypo>
      ],
      "line3": [],
    },
    {
      "id": 2,
      "role": "PM",
      "label": ["#고객 데이터 분석이 필요한", "#뷰티 커머스 스타트업"],
      "line1": [
        <CarouselTypo key="1-1"><b>쇼핑몰의 데이터를 분석</b>해주실 프로덕트 매니저</CarouselTypo>
      ],
      "line2": [
        <CarouselTypo key="2-1">- 뷰티 커머스 자사몰의 [고객 데이터를 추적할 수 있게 세팅]해주실 분</CarouselTypo>,
        <CarouselTypo key="2-2">- 데이터를 보고 [자사몰 사이트의 개선 제안]을 해주실 수 있는 분 </CarouselTypo>,
      ],
      "line3": [
        <CarouselTypo key="3-1"><b>📌 커머스 PM/PO 경력</b>이 있으신 분</CarouselTypo>,
        <CarouselTypo key="3-2"><b>📌 GA4, Amplitude 세팅</b>하실 수 있는 분</CarouselTypo>,
      ],
    },
  ],
}


export default function HowToWriteDialog({ position, open, closeDialog }) {
  const smUp = useResponsive('up', 'sm');
  const theme = useTheme();
  const carouselRef = useRef(null);

  const settings = {
    dots: true,
    arrows: true,
    autoplay: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    rtl: Boolean(theme.direction === 'rtl'),
    ...CarouselDots({
      rounded: true,
      sx: { mt: 2 },
    }),
  };

  const handleNext = async () => {
    carouselRef.current?.slickNext();
  };

  const handlePrevious = async () => {
    carouselRef.current?.slickPrev();
  };

  if (!position) return <></>
  if (!PROCESSES?.[position]) return <></>;

  return (
    <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm" fullScreen={!smUp}>
      <DialogContent sx={{ px: { xs: 3, sm: 4 }, pb: 0 }}>
        <Typography variant="h5" gutterBottom>
          🙋‍♀️ 업무 내용, 보통 이렇게 작성합니다.
        </Typography>
        <Typography variant="body1" sx={{ wordBreak: "keep-all" }}>
          실제 <DIOText /> 기업 고객이 작성한 업무 내용 예시입니다. 
        </Typography>
        <Paper>
          <CarouselArrows
            onNext={handleNext}
            onPrevious={handlePrevious}
          >
            <Slider ref={carouselRef} {...settings}>
              {PROCESSES[position].map((item) => (
                <CarouselItem key={item.id} item={item} />
              ))}
            </Slider>
          </CarouselArrows>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 3, sm: 4 }}}>
        <Button
          variant="contained"
          onClick={closeDialog}
          fullWidth={!smUp}
          size="large"
          sx={{ px: 5 }}
        >
          확인했어요
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function CarouselItem({ item }) {
  const theme = useTheme();

  return (
    <Paper
      sx={{
        p: 3,
        mt: 3,
        backgroundColor: "grey.200"
       }}>
      <Typography variant="subtitle1" gutterBottom>
        <BlueText>{item?.role}</BlueText> 업무 내용 예시
      </Typography>
      <Stack direction="row" display="flex" sx={{ gap: 1, flexWrap: "wrap" }}>
        {item?.label?.map((l, index) => (
          <Label variant="outlined"
            key={`label-${index}`}
            sx={{ color: "primary", borderColor: "grey.600", ...theme.typography.caption, borderRadius: 1.5 }}
          >
            {l}
          </Label>  
        ))}
      </Stack>
      <Paper
        sx={{ p: 3, my: 2 }}
      >
        <Stack spacing={2}>
          <Stack>
            {item.line1?.map((t) => t)}
          </Stack>
          <Stack>
            {item.line2?.map((t) => t)}
          </Stack>
          <Stack>
            {item.line3?.map((t) => t)}
          </Stack>
        </Stack>
      </Paper>
    </Paper>
  )
}
